/**
 * The boathouse door seen from inside the Nook (`hush.interior_door.v1`, hideout plan P5): the same plank frame and
 * reed thatch as outside, stone steps rising up into cool moonlight from the road, and a warm spill on the lowest
 * step from the room. Ground piece; decoration only (the return portal is authored separately).
 */
import { checkEnvelope, checkScope, el, mix, n, partGroup, poly, rngFor, type HideoutPieceArt, type PieceRecipe, type ResolvedPalette } from '../pieces/svg';

export type InteriorDoorRecipe = PieceRecipe<'hush.interior_door.v1', { steps: number }>;
export const INTERIOR_DOOR_FRAME = { w: 130, h: 180 } as const;

export function buildInteriorDoor(input: InteriorDoorRecipe, roles: ResolvedPalette, scope: string): HideoutPieceArt {
  checkEnvelope(input, 'hush.interior_door.v1');
  const steps = input.params.steps;
  if (!Number.isInteger(steps) || steps < 2 || steps > 5) throw new Error('hush.interior_door.v1: steps must be 2..5');
  checkScope(scope);
  const rng = rngFor(input);
  const { w, h } = INTERIOR_DOOR_FRAME;
  const parts: string[] = [];
  const open = { x: 30, y: 34, w: 70, h: h - 34 };
  const skyId = `${scope}_moon`;
  const defs = el('defs', {}, el('linearGradient', { id: skyId, x1: 0, y1: 0, x2: 0, y2: 1 },
    el('stop', { offset: 0, 'stop-color': mix(roles.waterLight, '#ffffff', 0.25) })
    + el('stop', { offset: 0.6, 'stop-color': roles.waterFar })
    + el('stop', { offset: 1, 'stop-color': mix(roles.waterFar, roles.outline, 0.5) })));

  // Moonlit opening with the reed fringe of the outside eave hanging across its top.
  let opening = el('rect', { x: open.x, y: open.y, width: open.w, height: open.h, fill: `url(#${skyId})` });
  let fringe = '';
  for (let i = 0; i < 12; i++) {
    const x = open.x + 2 + i * 5.8, len = 6 + rng(`fringe.${i}`) * 8;
    fringe += `M${n(x - 2.4)} ${n(open.y)}L${n(x)} ${n(open.y + len)}L${n(x + 2.4)} ${n(open.y)}`;
  }
  opening += el('path', { d: fringe, fill: roles.reedShadow, stroke: roles.outline, 'stroke-width': 0.5 });
  // Steps climb from the room floor up into the opening, each a little cooler.
  for (let i = 0; i < steps; i++) {
    const sh = 12, sw = open.w - 6 - i * 8;
    const y = h - (i + 1) * sh;
    const fill = mix(roles.stoneBase, roles.waterFar, i / steps * 0.6);
    opening += el('rect', { x: open.x + 3 + i * 4, y, width: sw, height: sh, rx: 2, fill, stroke: roles.outline, 'stroke-width': 0.9 });
    opening += el('path', { d: `M${n(open.x + 6 + i * 4)} ${n(y + 2)}H${n(open.x + sw - 2 + i * 4)}`, stroke: roles.stoneLight, 'stroke-width': 1, opacity: 0.6 });
  }
  opening += el('path', { d: poly([{ x: open.x + 4, y: h - 1 }, { x: open.x + open.w - 4, y: h - 1 }, { x: open.x + open.w - 14, y: h - 14 }, { x: open.x + 14, y: h - 14 }]),
    fill: roles.warmCore, opacity: 0.35 });
  let frame = el('rect', { x: open.x - 12, y: open.y - 14, width: open.w + 24, height: 14, fill: roles.woodShadow, stroke: roles.outline, 'stroke-width': 1.2 });
  for (const x of [open.x - 12, open.x + open.w]) {
    frame += el('rect', { x, y: open.y, width: 12, height: open.h - 0.6, fill: roles.woodBase, stroke: roles.outline, 'stroke-width': 1.1 });
    frame += el('path', { d: `M${n(x + 4)} ${n(open.y + 6)}V${n(h - 6)}`, stroke: roles.woodShadow, 'stroke-width': 0.9 });
  }
  // Thatch eave above the lintel, as seen from below.
  let thatch = el('path', { d: `M${n(4)} ${n(open.y - 14)}Q${n(w / 2)} ${n(open.y - 30)} ${n(w - 4)} ${n(open.y - 14)}L${n(w - 8)} ${n(open.y - 6)}L${n(8)} ${n(open.y - 6)}Z`,
    fill: roles.reedBase, stroke: roles.outline, 'stroke-width': 1.2 });
  for (let i = 0; i < 14; i++) {
    const x = 10 + i * 8.2;
    thatch += el('path', { d: `M${n(x)} ${n(open.y - 22 + Math.abs(i - 7) * 1.1)}l${n(1.5)} 12`, stroke: i % 2 ? roles.reedShadow : roles.reedLight, 'stroke-width': 1 });
  }
  const body = defs + partGroup(parts, 'interior-door-opening', opening) + partGroup(parts, 'interior-door-frame', frame)
    + partGroup(parts, 'interior-door-thatch', thatch);
  return {
    frame: { w, h }, body, parts,
    anchors: { support: { x: w / 2, y: h }, opening: { x: open.x + open.w / 2, y: open.y + open.h / 2 }, moonlight: { x: open.x + open.w / 2, y: open.y + 20 } },
  };
}

export const HUSH_INTERIOR_DOOR: InteriorDoorRecipe = {
  schema: 'hush.asset-recipe.v1', id: 'hush.interior_door.boathouse.v1', generatorId: 'hush.interior_door.v1',
  seed: 20067018, paletteId: 'nook_warm', params: { steps: 4 },
};
